import { isSmallScreenAtom } from '@/atoms/screenSize'
import { HugeiconsIcon } from '@hugeicons/react'
import { EllipsisIcon } from '@hugeicons/core-free-icons'
import { Event } from '@nostr/tools/wasm'
import { useMemo, useState } from 'react'
import { useAtomValue } from 'jotai'
import PostEditor from '../PostEditor'
import { DesktopMenu } from './DesktopMenu'
import { MobileMenu } from './MobileMenu'
import RawEventDialog from './RawEventDialog'
import { MenuAction, SubMenuAction, useMenuActions } from './useMenuActions'

export default function NoteOptions({
  event,
  className,
  extraActions,
  customTrigger
}: {
  event: Event
  className?: string
  extraActions?: MenuAction[]
  customTrigger?: React.ReactNode
}) {
  const isSmallScreen = useAtomValue(isSmallScreenAtom)
  const [isRawEventDialogOpen, setIsRawEventDialogOpen] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [showSubMenu, setShowSubMenu] = useState(false)
  const [activeSubMenu, setActiveSubMenu] = useState<SubMenuAction[]>([])
  const [subMenuTitle, setSubMenuTitle] = useState('')
  const [isEditorOpen, setIsEditorOpen] = useState(false)
  const [editingEvent, setEditingEvent] = useState<Event | undefined>()

  const menuActions = useMenuActions({
    event,
    closeDrawer,
    openEditor,
    showSubMenuActions,
    setIsRawEventDialogOpen,
    isSmallScreen
  })

  const allActions = useMemo(() => {
    if (!extraActions?.length) return menuActions

    menuActions[0].separator = true
    return [...extraActions, ...menuActions]
  }, [menuActions, extraActions])

  const trigger = customTrigger ? (
    <button
      type="button"
      className="inline-flex items-center"
      onClick={() => setIsDrawerOpen(true)}
    >
      {customTrigger}
    </button>
  ) : (
    <button
      className="flex items-center text-muted-foreground hover:text-foreground pl-2 h-full"
      onClick={() => setIsDrawerOpen(true)}
    >
      <HugeiconsIcon icon={EllipsisIcon} />
    </button>
  )

  return (
    <div className={className} onClick={(e) => e.stopPropagation()}>
      {isSmallScreen ? (
        <MobileMenu
          menuActions={allActions}
          trigger={trigger}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          showSubMenu={showSubMenu}
          activeSubMenu={activeSubMenu}
          subMenuTitle={subMenuTitle}
          closeDrawer={closeDrawer}
          goBackToMainMenu={goBackToMainMenu}
        />
      ) : (
        <DesktopMenu menuActions={allActions} trigger={trigger} />
      )}

      <RawEventDialog
        event={event}
        isOpen={isRawEventDialogOpen}
        onClose={() => setIsRawEventDialogOpen(false)}
      />
      <PostEditor
        defaultContent={editingEvent?.content}
        editingEvent={editingEvent}
        open={isEditorOpen}
        setOpen={closeEditor}
      />
    </div>
  )

  function closeDrawer() {
    setIsDrawerOpen(false)
    setShowSubMenu(false)
  }

  function goBackToMainMenu() {
    setShowSubMenu(false)
  }

  function showSubMenuActions(subMenu: SubMenuAction[], title: string) {
    setActiveSubMenu(subMenu)
    setSubMenuTitle(title)
    setShowSubMenu(true)
  }

  function openEditor() {
    setEditingEvent(event)
    setIsEditorOpen(true)
  }

  function closeEditor(open: boolean) {
    setIsEditorOpen(open)
    if (!open) {
      setEditingEvent(undefined)
    }
  }
}
